import { useEffect, useState } from 'react';
import { clearAuthToken, getMe, getSession, tokenExists } from '../api/auth';
import {
  getSelectedCompanyId,
  persistSelectedCompanyId,
  subscribeSelectedCompany,
} from '../utils/selectedCompany';

let cachedSession = null;
let sessionInflight = null;
const cachedUsersByCompany = new Map();
const inflightUsersByCompany = new Map();
let cacheGeneration = 0;

const invalidateCurrentUserCache = () => {
  cacheGeneration += 1;
  cachedSession = null;
  sessionInflight = null;
  cachedUsersByCompany.clear();
  inflightUsersByCompany.clear();
};

const unwrap = (payload) => payload?.data ?? payload ?? {};

export const normalizeSession = (payload) => {
  const session = unwrap(payload);
  const identity = session.identity || session.user || {};
  const platformRoles = Array.from(new Set([
    ...(Array.isArray(session.platformRoles) ? session.platformRoles : []),
    ...(Array.isArray(identity.platformRoles) ? identity.platformRoles : []),
  ].map((role) => (typeof role === 'string' ? role : role?.name)).filter(Boolean)));
  const companiesSource = session.companies
    || session.accessibleCompanies
    || identity.companies
    || identity.Companies
    || [];
  const companies = (Array.isArray(companiesSource) ? companiesSource : [])
    .map((company) => {
      const details = company.Company || company.company || company;
      const id = details.id ?? company.companyId;
      return {
        ...company,
        ...details,
        id,
        name: details.name || company.companyName || `Empresa ${id}`,
        status: details.status || company.status || (company.isEnabled === false ? 'disabled' : 'active'),
      };
    })
    .filter((company) => company.id !== null && company.id !== undefined)
    .filter((company) => company.status === 'active' && !company.deletedAt && company.isDeleted !== true);

  return { identity, platformRoles, companies };
};

const mergeEffectiveUser = ({ identity, platformRoles, companies }, effectiveUser, companyId) => {
  const activeUser = unwrap(effectiveUser)?.user || unwrap(effectiveUser);
  const roles = Array.from(new Set([
    ...(Array.isArray(activeUser?.roles) ? activeUser.roles : []),
    ...(Array.isArray(activeUser?.Roles) ? activeUser.Roles : []),
    ...platformRoles,
  ].map((role) => (typeof role === 'string' ? role : role?.name)).filter(Boolean)));
  return {
    ...identity,
    ...(activeUser || {}),
    roles,
    platformRoles,
    companies,
    selectedCompanyId: companyId || '',
  };
};

const fetchSession = async ({ refresh = false } = {}) => {
  if (refresh) {
    invalidateCurrentUserCache();
  }
  if (cachedSession) return cachedSession;
  if (!sessionInflight) {
    const generation = cacheGeneration;
    const request = getSession()
      .then((data) => {
        const normalizedSession = normalizeSession(data);
        if (generation === cacheGeneration) cachedSession = normalizedSession;
        if (sessionInflight === request) sessionInflight = null;
        return normalizedSession;
      })
      .catch((error) => {
        if (sessionInflight === request) sessionInflight = null;
        throw error;
      });
    sessionInflight = request;
  }
  return sessionInflight;
};

const fetchEffectiveUser = async (companyId) => {
  const key = String(companyId || '');
  if (!key) return null;
  if (cachedUsersByCompany.has(key)) return cachedUsersByCompany.get(key);
  if (!inflightUsersByCompany.has(key)) {
    const generation = cacheGeneration;
    const request = getMe()
      .then((data) => {
        if (generation === cacheGeneration) cachedUsersByCompany.set(key, data);
        if (inflightUsersByCompany.get(key) === request) inflightUsersByCompany.delete(key);
        return data;
      })
      .catch((error) => {
        if (inflightUsersByCompany.get(key) === request) inflightUsersByCompany.delete(key);
        throw error;
      });
    inflightUsersByCompany.set(key, request);
  }
  return inflightUsersByCompany.get(key);
};

const resolveCompanyId = (companies) => {
  const selectedCompanyId = getSelectedCompanyId();
  const allowedIds = new Set(companies.map((company) => String(company.id)));
  if (selectedCompanyId && allowedIds.has(String(selectedCompanyId))) return selectedCompanyId;
  if (companies.length === 1) {
    return persistSelectedCompanyId(companies[0].id, { clearBranch: true });
  }
  if (selectedCompanyId) persistSelectedCompanyId('', { clearBranch: true });
  return '';
};

const loadCurrentUser = async ({ refreshSession = false } = {}) => {
  const session = await fetchSession({ refresh: refreshSession });
  const companyId = resolveCompanyId(session.companies);
  if (!companyId) {
    return {
      session,
      companyId: '',
      user: mergeEffectiveUser(session, { permissions: [] }, ''),
      companySelectionRequired: session.companies.length > 1,
    };
  }
  const effectiveUser = await fetchEffectiveUser(companyId);
  return {
    session,
    companyId,
    user: mergeEffectiveUser(session, effectiveUser, companyId),
    companySelectionRequired: false,
  };
};

export const clearCurrentUserCache = ({ notify = false } = {}) => {
  invalidateCurrentUserCache();
  if (notify) window.dispatchEvent(new Event('currentUserAccessChanged'));
};

const useCurrentUser = () => {
  const [state, setState] = useState({ user: null, session: cachedSession, status: 'idle' });

  useEffect(() => {
    let mounted = true;
    let loadVersion = 0;

    const load = ({ refreshSession = false } = {}) => {
      const version = ++loadVersion;
      if (!tokenExists()) {
        setState({ user: null, session: null, status: 'unauthorized' });
        return;
      }
      setState((current) => ({ ...current, status: 'loading', error: null }));
      loadCurrentUser({ refreshSession })
        .then((result) => {
          if (!mounted || version !== loadVersion) return;
          setState({
            ...result,
            companies: result.session.companies,
            status: 'ready',
          });
        })
        .catch((error) => {
          if (!mounted || version !== loadVersion) return;
          const statusCode = Number(error?.response?.status || error?.status);
          if (statusCode === 401) clearAuthToken();
          setState({
            user: null,
            session: null,
            status: statusCode === 401 ? 'unauthorized' : 'error',
            error,
          });
        });
    };

    load();
    const refreshAccess = () => load();
    window.addEventListener('currentUserAccessChanged', refreshAccess);
    const unsubscribe = subscribeSelectedCompany((companyId) => {
      if (!companyId && cachedSession) {
        clearCurrentUserCache();
      }
      load();
    });

    return () => {
      mounted = false;
      unsubscribe();
      window.removeEventListener('currentUserAccessChanged', refreshAccess);
    };
  }, []);

  return state;
};

export default useCurrentUser;
